import React, { useEffect, useState } from "react";
import Router from "next/router";
import dynamic from "next/dynamic";
import { useForm } from "react-hook-form";
import { CategoryTitle, TitleWrap } from "styles/AddRecipe.styled";
import Input from "shared/Input";
import { colors } from "styles/colors";
import Button from "shared/Button";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSelector, useDispatch } from "react-redux";
const InputPhoto = dynamic(() => import("shared/InputPhoto"), { ssr: false });
import { clearError, setOwner } from "store/userReducer";
import { patchOwner } from "store/userReducer/userActions";
import { getOwner, userSubmitting, userErrors } from "store/userReducer/userSelectors";
import { WrapMessage } from "shared/Input/styled";
import TextField from "shared/TextField";
import DOB from "shared/CommonForm/DateAndBirth";
import LayoutInput from "shared/CommonForm/LayoutInput";
import omit from "lodash/omit";
import Checkbox from "shared/Checkbox";

import ModalEditor from "components/ModalEditor/ModalEditor";
import { deleteUserAvatar, uploadProfileImage } from "api/api";

import {
  SaveBtn,
  ImageWrapper,
  InfoWrap,
  SettingsWrap,
  InfoImage,
  InfoContent,
  InfoTitle,
  InfoDescription,
} from "./styled";
import { useIsMounted } from "helpers/hooks/useIsMounted";
import { createAlert } from "store/alert";
import { useRenderDevice } from "helpers/hooks/useRenderDevice";

const schema = yup.object().shape({
  first_name: yup.string().min(1, "enter your first name"),
  last_name: yup.string().min(1, "enter your last name"),
  business_name: yup.string().min(0, "enter your business name"),
  work_place: yup.string().min(0, "enter your workspace"),
  location: yup.string().min(0, "too short"),
  bio: yup.string().min(0, "too short").max(512, "Ensure this field has no more than 512 characters."),
  image: yup.mixed(),
  facebook: yup.string(),
  twitter: yup.string(),
  instagram: yup.string(),
  tiktok: yup.string(),
  youtube: yup.string(),
  website: yup.string(),
});

const Info = () => {
  const [photo, setPhoto] = useState("");
  const [checked, setChecked] = useState(false);
  const [cropPhoto, setCropPhoto] = useState(null);
  const dispatch = useDispatch();
  const user = useSelector(getOwner);
  const submitting = useSelector(userSubmitting);
  const responseErrors = useSelector(userErrors);

  const isMounted = useIsMounted();

  const isDesktop = useRenderDevice({ min: 769 });

  const { register, handleSubmit, errors, reset, control, setValue } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      business_name: "",
      work_place: "",
      location: "",
      image: "",
      facebook: user.facebook_url,
      twitter: user.twitter_url,
      instagram: user.instagram_url,
      tiktok: user.tiktok_url,
      youtube: user.youtube_url,
      website: user.website_link,
    },
    resolver: yupResolver(schema),
  });

  const onDeleteClick = async () => {
    try {
      await deleteUserAvatar(user.id);
      dispatch(setOwner({ main_image: null }));
      setPhoto(null);
      setCropPhoto(null);
    } catch (err) {
      console.log(err);
    }
  };

  const isErrorExist = (err) => {
    return Object.keys(errors).includes(err);
  };

  const onSubmit = async (data, e) => {
    e.preventDefault();
    const newData = omit(data, ["day", "month", "year"]);
    const birthdate = `${data.year !== undefined ? data.year + "-" : ""}${
      data.month !== undefined ? data.month + "-" : ""
    }${data.day !== undefined ? data.day : ""}`;

    const isPhoto = cropPhoto || photo;
    try {
      if (cropPhoto || (!photo?.startsWith("http") && photo)) {
        let res = await uploadProfileImage(user.id, isPhoto);
        dispatch(setOwner(res.data));
      }

      await dispatch(
        patchOwner({
          data: {
            ...newData,
            email: user.email,
            birthdate: birthdate || null,
            show_business_name: checked,
            facebook_url: data.facebook,
            twitter_url: data.twitter,
            instagram_url: data.instagram,
            tiktok_url: data.tiktok,
            youtube_url: data.youtube,
            website_link: data.website,
          },
          id: user.id,
        })
      );

      if (user.slug) Router.push(`/${user.slug}`);
      else Router.push(`/users/${user.id}`);
    } catch (err) {
      dispatch(createAlert({ type: "error", message: err.response.data.main_image[0] }));
    }
  };

  useEffect(() => {
    setChecked(user.show_business_name);
    reset({
      first_name: user.first_name,
      last_name: user.last_name,
      business_name: user.business_name,
      work_place: user.work_place,
      location: user.location,
      image: user.main_image,
      bio: user.bio,
      gender: user.gender,
      slug: user.slug,
      facebook: user.facebook_url,
      twitter: user.twitter_url,
      instagram: user.instagram_url,
      tiktok: user.tiktok_url,
      youtube: user.youtube_url,
      website: user.website_link,
    });
    if (user.main_image) {
      setPhoto(user.main_image);
    }
  }, [user]);

  return (
    <SettingsWrap>
      {responseErrors.data && <WrapMessage className="global">something wrong</WrapMessage>}
      <ModalEditor
        photo={photo}
        setCropPhoto={(img) => {
          setCropPhoto(img);
        }}
      />
      {isDesktop && (
        <TitleWrap>
          <CategoryTitle>Profile Image</CategoryTitle>
        </TitleWrap>
      )}
      {isMounted && (
        <InfoImage>
          <ImageWrapper photo={cropPhoto || photo}>
            <InputPhoto
              photo={cropPhoto || photo}
              setPhoto={(img, callback) => {
                setPhoto(img);
                setCropPhoto(null);
                callback();
              }}
              fileType="image/*"
              uploadImage
              name="main_image"
              iconId={`/icons/uploadProfile2.svg`}
              desc="Upload Profile Image"
              infotext="We suggest uploading images below 10 MB and in .jpg or .png format"
              iconBtn="uploadBtn"
              display="hidden"
              crop
              deleteFile={onDeleteClick}
            />
          </ImageWrapper>

          <InfoContent>
            <InfoTitle>Upload Profile Image</InfoTitle>
            <InfoDescription>We suggest uploading images below 10 MB and in .jpg or .png format</InfoDescription>
          </InfoContent>
        </InfoImage>
      )}

      {isDesktop && (
        <TitleWrap spacing={true.toString()}>
          <CategoryTitle>Personal Info</CategoryTitle>
        </TitleWrap>
      )}
      <form onChange={() => responseErrors.data && dispatch(clearError())}>
        <InfoWrap>
          <LayoutInput title="First Name">
            <Input
              reff={register}
              name="first_name"
              type="text"
              error={!!errors.first_name}
              errorMessage={errors?.first_name?.message}
            />
          </LayoutInput>
          <LayoutInput title="Last Name">
            <Input
              reff={register}
              name="last_name"
              type="text"
              error={!!errors.last_name}
              errorMessage={errors?.last_name?.message}
            />
          </LayoutInput>
          {user.is_creator && (
            <LayoutInput fontsize gap isReadonly={!checked && "readonly"} title="Business Name" readonly="readonly">
              <Checkbox checked={checked} onChange={() => setChecked(!checked)}>
                Use my business name for this account
              </Checkbox>
              <Input
                reff={register}
                name="business_name"
                type="text"
                error={!!errors.business_name}
                errorMessage={errors?.business_name?.message}
                readonly={!checked && "readonly"}
              />
            </LayoutInput>
          )}
          <LayoutInput fontsize title="About me" textarea>
            <TextField
              reff={register}
              name="bio"
              type="text"
              rows={8}
              error={!!errors.bio}
              errorMessage={errors?.bio?.message}
            />
          </LayoutInput>
          <LayoutInput title="Location">
            <Input
              reff={register}
              name="location"
              type="text"
              error={!!errors.location}
              errorMessage={errors?.location?.message}
            />
          </LayoutInput>
          <LayoutInput dropDownStyle title="Date of birth" display="flex">
            <DOB
              isErrorExist={isErrorExist}
              isServerErrorExist={isErrorExist}
              control={control}
              setValue={setValue}
              birth={user.birthdate?.split("-")}
            />
          </LayoutInput>
          {user.is_creator && (
            <>
              <LayoutInput title="Facebook">
                <Input
                  reff={register}
                  name="facebook"
                  type="text"
                  error={!!errors.facebook}
                  errorMessage={errors?.facebook?.message}
                  placeholder="Paste link on your Facebook profile"
                />
              </LayoutInput>
              <LayoutInput title="Twitter">
                <Input
                  reff={register}
                  name="twitter"
                  type="text"
                  error={!!errors.twitter}
                  errorMessage={errors?.twitter?.message}
                  placeholder="Paste link on your Twitter profile"
                />
              </LayoutInput>
              <LayoutInput title="Instagram">
                <Input
                  reff={register}
                  name="instagram"
                  type="text"
                  error={!!errors.instagram}
                  errorMessage={errors?.instagram?.message}
                  placeholder="Paste link on your Instagram profile"
                />
              </LayoutInput>
              <LayoutInput title="TikTok">
                <Input
                  reff={register}
                  name="tiktok"
                  type="text"
                  error={!!errors.tiktok}
                  errorMessage={errors?.tiktok?.message}
                  placeholder="Paste link on your Tiktok profile"
                />
              </LayoutInput>
              <LayoutInput title="YouTube">
                <Input
                  reff={register}
                  name="youtube"
                  type="text"
                  error={!!errors.youtube}
                  errorMessage={errors?.youtube?.message}
                  placeholder="Paste link on your YouTube profile"
                />
              </LayoutInput>
              <LayoutInput title="Website">
                <Input
                  reff={register}
                  name="website"
                  type="text"
                  error={!!errors.website}
                  errorMessage={errors?.website?.message}
                  placeholder="Paste link on your Website"
                />
              </LayoutInput>
            </>
          )}
        </InfoWrap>
        <SaveBtn>
          <Button
            bdButton={colors.WHITE}
            color={colors.RED}
            textColor={colors.WHITE}
            text="Save"
            onClick={handleSubmit((data, e) => onSubmit(data, e))}
            stylType="primary"
            disabled={submitting}
          />
        </SaveBtn>
      </form>
    </SettingsWrap>
  );
};

export default Info;
